import { useApolloClient } from '@apollo/client/react';
import {
  ActionIcon,
  AppShell,
  Burger,
  Group,
  NavLink,
  ScrollArea,
  SegmentedControl,
  Text,
  Title,
  Tooltip,
  useComputedColorScheme,
  useMantineColorScheme,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconApps,
  IconBooks,
  IconFlask,
  IconLayoutDashboard,
  IconLogout,
  IconMail,
  IconMoon,
  IconSettings,
  IconSun,
  IconUsers,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { NavLink as RouterNavLink, Outlet, useLocation } from 'react-router';

import { useAuth } from '../auth/context';
import { currentLocale, SUPPORTED_LOCALES } from '../i18n';
import { COLLECTIONS, useLabels } from '../labels';

export function AppLayout() {
  const [opened, { toggle, close }] = useDisclosure();
  const { admin, signOut } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();
  const labels = useLabels();

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{ width: 240, breakpoint: 'sm', collapsed: { mobile: !opened } }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            <Title order={4}>talumi</Title>
            <Text size="sm" c="dimmed" visibleFrom="sm">
              {t('app.subtitle')}
            </Text>
          </Group>

          <Group gap="xs" wrap="nowrap">
            <Text size="sm" c="dimmed" visibleFrom="sm">
              {admin?.email}
            </Text>
            <LanguageSwitcher />
            <ColorSchemeToggle />
            <Tooltip label={t('app.signOut')}>
              <ActionIcon
                variant="default"
                size="lg"
                aria-label={t('app.signOut')}
                onClick={signOut}
              >
                <IconLogout size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="xs">
        <ScrollArea>
          <NavLink
            component={RouterNavLink}
            to="/"
            end
            label={t('nav.dashboard')}
            leftSection={<IconLayoutDashboard size={18} />}
            active={location.pathname === '/'}
            onClick={close}
          />
          <NavLink
            component={RouterNavLink}
            to="/users"
            label={t('nav.users')}
            leftSection={<IconUsers size={18} />}
            active={location.pathname.startsWith('/users')}
            onClick={close}
          />
          <NavLink
            component={RouterNavLink}
            to="/stories"
            label={t('nav.stories')}
            leftSection={<IconBooks size={18} />}
            active={location.pathname.startsWith('/stories')}
            onClick={close}
          />
          <NavLink
            label={t('nav.catalog')}
            leftSection={<IconBooks size={18} />}
            defaultOpened
            childrenOffset={28}
          >
            {COLLECTIONS.map((collection) => (
              <NavLink
                key={collection}
                component={RouterNavLink}
                to={`/catalog/${collection}`}
                label={labels.collection(collection)}
                active={location.pathname === `/catalog/${collection}`}
                onClick={close}
              />
            ))}
          </NavLink>
          <NavLink
            component={RouterNavLink}
            to="/write-us"
            label={t('nav.writeUs')}
            leftSection={<IconMail size={18} />}
            active={location.pathname.startsWith('/write-us')}
            onClick={close}
          />
          <NavLink
            component={RouterNavLink}
            to="/more-apps"
            label={t('nav.moreApps')}
            leftSection={<IconApps size={18} />}
            active={location.pathname.startsWith('/more-apps')}
            onClick={close}
          />
          <NavLink
            component={RouterNavLink}
            to="/model-test"
            label={t('nav.modelTest')}
            leftSection={<IconFlask size={18} />}
            active={location.pathname.startsWith('/model-test')}
            onClick={close}
          />
          <NavLink
            component={RouterNavLink}
            to="/settings"
            label={t('nav.settings')}
            leftSection={<IconSettings size={18} />}
            active={location.pathname.startsWith('/settings')}
            onClick={close}
          />
        </ScrollArea>
      </AppShell.Navbar>

      <AppShell.Main>
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}

/**
 * Russian or English, for the interface *and* for the API.
 *
 * The store is reset rather than merely re-read: every cached answer was
 * fetched under the old `Accept-Language`, so a catalogue row or an error
 * already in the cache would stay in the previous language until something
 * else happened to refetch it. i18next's detector writes the choice to
 * localStorage on its own, which is what makes it survive a reload.
 */
function LanguageSwitcher() {
  const { t, i18n } = useTranslation();
  const client = useApolloClient();

  return (
    <SegmentedControl
      size="xs"
      aria-label={t('app.language')}
      value={currentLocale()}
      data={SUPPORTED_LOCALES.map((locale) => ({
        value: locale,
        label: locale.toUpperCase(),
      }))}
      onChange={(value) => {
        void i18n.changeLanguage(value).then(() =>
          // A refetch of a screen that has since unmounted rejects; the
          // language has already changed by then, which is what matters.
          client.resetStore().catch(() => undefined),
        );
      }}
    />
  );
}

function ColorSchemeToggle() {
  const { t } = useTranslation();
  const { setColorScheme } = useMantineColorScheme();
  // `getInitialValueInEffect: false` keeps this in step with the value Mantine
  // resolved at boot, so the icon does not flip on the first paint.
  const computed = useComputedColorScheme('light', { getInitialValueInEffect: false });
  const next = computed === 'dark' ? 'light' : 'dark';

  return (
    <Tooltip label={next === 'dark' ? t('app.theme.dark') : t('app.theme.light')}>
      <ActionIcon
        variant="default"
        size="lg"
        aria-label={t('app.theme.toggle')}
        onClick={() => {
          setColorScheme(next);
        }}
      >
        {computed === 'dark' ? <IconSun size={18} /> : <IconMoon size={18} />}
      </ActionIcon>
    </Tooltip>
  );
}
